import * as React from 'react';
import styled from 'styled-components';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import Grid from 'semantic-ui-react/dist/commonjs/collections/Grid';
import { MessageSourceProps, withMessages } from 'react-message-source';
import Icon from 'semantic-ui-react/dist/commonjs/elements/Icon';
import { getAllDignities } from 'services/dignityServices';
import { MandateConfigDto, MandateQualitativeDto, TarmedDignityDto } from 'ts-types/api.types';
import { Accordion } from 'semantic-ui-react';

const StyledTableLeft = styled(Grid.Column)`
    padding-left: 1rem !important;
    padding-right: 0 !important;
    flex: 50 50 auto;
`;

const StyledButtons = styled(Grid.Column)`
  padding: 0 !important;
  margin: 0 4px 0 4px !important;
  flex: 1 1 auto;
`;

const StyledTableRight = styled(Grid.Column)`
    padding-right: 1rem !important;
    padding-left: 0 !important;
    flex: 50 50 auto;
`;

const StyledRow = styled(Grid.Row)`
    height: 326px !important;
`;

const StyledIcon = styled(Icon)`
    margin: 0 !important;
    cursor: pointer;
`;

const ActionsRow = styled('div')`
    margin-top: 60px;
`;

const StyledHeader = styled('div')`
    font-weight: bold;
    margin-bottom: 10px;
    font-size: 14px;
`;

const StyledBtnContainer = styled.div`
  margin-bottom: 5px;
  white-space: nowrap !important;
`;

const StyledNoRowsMessage = styled('div')`
    background-color: #ddd;
    border: 1px solid #e0e0e0;
    border-top: none;
    height: 42px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    overflow: hidden;
    padding: 0 10px;
    font-size: 1rem;
    display: flex;
    flex-direction: row;
    align-items: center;
    font-style: italic
`;

const StyledGrid = styled(Grid)`
  margin-top: 0 !important;
  margin-bottom: 40px !important;
  height: 100%;
`;

interface Props extends MessageSourceProps {
  mandate: MandateConfigDto,
  setMandateQualitatives: (mandateQualitatives: MandateQualitativeDto[]) => {}
}

interface State {
  assignedDignities: TarmedDignityDto[],
  unassignedDignities: TarmedDignityDto[],
  selectedAssignedDignities: Array<number>,
  selectedUnassignedDignities: Array<number>,
  activeIndex: number
}

class DignitySection extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const mandateQualitatives = props.mandate.mandateQualitatives ? props.mandate.mandateQualitatives : [];

    this.state = {
      assignedDignities: [],
      unassignedDignities: [],
      selectedAssignedDignities: [],
      selectedUnassignedDignities: [],
      activeIndex: -1
    };

    getAllDignities()
        .then(dignities => {
          let assignedDignities: TarmedDignityDto[] = [];
          let unassignedDignities: TarmedDignityDto[] = [];

          dignities.sort((a, b) => {
            return (a.active === b.active) ? 0 : a.active ? -1 : 1;
          });

          dignities.forEach(dignity => {
            if (!assignedDignities.find(x => x.code === dignity.code)
                && !unassignedDignities.find(x => x.code === dignity.code)) {
              if (mandateQualitatives.find(mq => mq.code === dignity.code)) {
                assignedDignities.push(dignity);
              } else if (dignity.active) {
                unassignedDignities.push(dignity);
              }
            }
          });

          this.setMandateQualitatives(assignedDignities);

          this.setState({
            assignedDignities: assignedDignities,
            unassignedDignities: unassignedDignities
          })

        });
  }

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {
      assignedDignities, unassignedDignities, activeIndex,
      selectedAssignedDignities, selectedUnassignedDignities
    } = this.state;

    return (
        <Accordion fluid>
          <Accordion.Title
              active={activeIndex === 0}
              index={0}
              onClick={this.toggleAccordion}
          >
            <h3><Icon name='dropdown'/>{getMessage('mandate.dignities')}</h3>
          </Accordion.Title>
          <Accordion.Content active={activeIndex === 0}>
            <StyledGrid>
              <StyledRow>
                <StyledTableLeft>
                  <StyledHeader>{getMessage("mandate.availableDignities")}</StyledHeader>
                  <VirtualizedTable
                      rowCount={unassignedDignities.length}
                      rowGetter={this.unassignedDignitiesRowGetter}
                      rowRenderer={this.unassignedDignitiesRowRenderer}
                      noRowsRenderer={() => this.noRowsRederer("noAvailableDignitiesLeft")}
                      columns={[
                        {
                          width: 120,
                          label: getMessage("mandate.code"),
                          dataKey: "code"
                        },
                        {
                          width: 100,
                          label: getMessage("mandate.type"),
                          dataKey: "type"
                        },
                        {
                          width: 500,
                          label: getMessage("mandate.description"),
                          dataKey: "text"
                        }
                      ]}
                  />
                </StyledTableLeft>
                <StyledButtons textAlign='center' verticalAlign='middle'>
                  <ActionsRow>
                    <StyledBtnContainer>
                      <StyledIcon bordered
                                  onClick={this.selectAllUnassignedDignities}
                                  name='clipboard list'
                      />
                      <StyledIcon bordered
                                  onClick={this.selectAllAssignedDignities}
                                  name='clipboard list'
                      />
                    </StyledBtnContainer>
                    <StyledBtnContainer>
                      <StyledIcon bordered
                                  onClick={this.unselectAllUnassignedDignities}
                                  name='clipboard outline'
                      />
                      <StyledIcon bordered
                                  onClick={this.unselectAllAssignedDignities}
                                  name='clipboard outline'
                      />
                    </StyledBtnContainer>
                    <StyledBtnContainer>
                      <StyledIcon bordered
                                  disabled={selectedUnassignedDignities.length <= 0}
                                  onClick={this.assignDignites}
                                  name='angle double right'
                      />
                      <StyledIcon bordered
                                  disabled={selectedAssignedDignities.length <= 0}
                                  onClick={this.unassignDignites}
                                  name='angle double left'
                      />
                    </StyledBtnContainer>
                  </ActionsRow>
                </StyledButtons>
                <StyledTableRight>
                  <StyledHeader>{getMessage("mandate.activeDignities")}</StyledHeader>
                  <VirtualizedTable
                      rowCount={assignedDignities.length}
                      rowGetter={this.assignedDignitiesRowGetter}
                      rowRenderer={this.assignedDignitiesRowRenderer}
                      noRowsRenderer={() => this.noRowsRederer("noActiveDignities")}
                      columns={[
                        {
                          width: 120,
                          label: getMessage("mandate.code"),
                          dataKey: "code"
                        },
                        {
                          width: 100,
                          label: getMessage("mandate.type"),
                          dataKey: "type"
                        },
                        {
                          width: 500,
                          label: getMessage("mandate.description"),
                          dataKey: "text"
                        }
                      ]}
                  />
                </StyledTableRight>
              </StyledRow>
            </StyledGrid>
          </Accordion.Content>
        </Accordion>
    );
  }

  toggleAccordion = (e: any, titleProps: any) => {
    const {index} = titleProps;
    const {activeIndex} = this.state;
    const newIndex = activeIndex === index ? -1 : index;

    this.setState({activeIndex: newIndex})
  };

  unselectAllUnassignedDignities = () => {
    this.setState({
      selectedUnassignedDignities: []
    })
  };

  unselectAllAssignedDignities = () => {
    this.setState({
      selectedAssignedDignities: []
    })
  };

  selectAllUnassignedDignities = () => {
    const {unassignedDignities} = this.state;

    const newSelectedUnassignedDignities = Array.apply(null, Array(unassignedDignities.length))
        .map((_, i) => i);

    this.setState({
      selectedUnassignedDignities: newSelectedUnassignedDignities
    })
  };

  selectAllAssignedDignities = () => {
    const {assignedDignities} = this.state;

    const newSelectedAssignedDignities = Array.apply(null, Array(assignedDignities.length))
        .map((_, i) => i);

    this.setState({
      selectedAssignedDignities: newSelectedAssignedDignities
    })
  };

  onUnassignedDignitiesRowClick = (event: any, index: number) => {
    const {selectedUnassignedDignities} = this.state;

    if (event.ctrlKey) {
      if (selectedUnassignedDignities.includes(index)) {
        this.setState({
          selectedUnassignedDignities: selectedUnassignedDignities.filter(i => i !== index)
        });
      } else {
        let newSelectedUnassignedDignities = Array.from(selectedUnassignedDignities);
        newSelectedUnassignedDignities.push(index);
        this.setState({selectedUnassignedDignities: newSelectedUnassignedDignities});
      }
    } else {
      this.setState({selectedUnassignedDignities: [index]});
    }
  };

  onAssignedDignitiesRowClick = (event: any, index: number) => {
    const {selectedAssignedDignities} = this.state;

    if (event.ctrlKey) {
      if (selectedAssignedDignities.includes(index)) {
        this.setState({
          selectedAssignedDignities: selectedAssignedDignities.filter(i => i !== index)
        });
      } else {
        let newSelectedAssignedDignities = Array.from(selectedAssignedDignities);
        newSelectedAssignedDignities.push(index);
        this.setState({selectedAssignedDignities: newSelectedAssignedDignities});
      }
    } else {
      this.setState({selectedAssignedDignities: [index]});
    }
  };

  unassignedDignitiesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {selectedUnassignedDignities} = this.state;
    if (selectedUnassignedDignities.includes(index)) {
      style = {...style, backgroundColor: "#ddd"}
    }
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onClick={(e) => this.onUnassignedDignitiesRowClick(e, index)}
        >
          {columns}
        </div>
    );
  };

  unassignedDignitiesRowGetter = ({index}: any) => {
    const {unassignedDignities} = this.state;
    Object.assign(unassignedDignities[index], {index: index + 1});

    return unassignedDignities[index]
  };

  assignedDignitiesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {selectedAssignedDignities} = this.state;
    if (selectedAssignedDignities.includes(index)) {
      style = {...style, backgroundColor: "#ddd"}
    }
    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onClick={(e) => this.onAssignedDignitiesRowClick(e, index)}
        >
          {columns}
        </div>
    );
  };

  assignedDignitiesRowGetter = ({index}: any) => {
    const {assignedDignities} = this.state;
    Object.assign(assignedDignities[index], {index: index + 1});

    return assignedDignities[index]
  };

  unassignDignites = () => {
    const {assignedDignities, unassignedDignities, selectedAssignedDignities} = this.state;

    let newAssignedDignities = Array.from(assignedDignities);
    let newUnassignedDignities = Array.from(unassignedDignities);
    let newSelectedAssignedDignities = Array.from(selectedAssignedDignities);

    while (newSelectedAssignedDignities.length > 0) {
      const index = newSelectedAssignedDignities.pop()!;

      newUnassignedDignities.push(assignedDignities[index]);
      newAssignedDignities.splice(index, 1);
    }

    newUnassignedDignities.sort((a, b) => a.code > b.code ? 1 : a.code === b.code ? 0 : -1);
    this.setMandateQualitatives(newAssignedDignities);

    this.setState({
      unassignedDignities: newUnassignedDignities,
      assignedDignities: newAssignedDignities,
      selectedAssignedDignities: [],
      selectedUnassignedDignities: []
    });

  };

  assignDignites = () => {
    const {assignedDignities, unassignedDignities, selectedUnassignedDignities} = this.state;

    let newAssignedDignities = Array.from(assignedDignities);
    let newUnassignedDignities = Array.from(unassignedDignities);
    let newSelectedUnassignedDignities = Array.from(selectedUnassignedDignities);

    while (newSelectedUnassignedDignities.length > 0) {
      const index = newSelectedUnassignedDignities.pop()!;

      newAssignedDignities.push(unassignedDignities[index]);
      newUnassignedDignities.splice(index, 1);
    }

    newAssignedDignities.sort((a, b) => a.code > b.code ? 1 : a.code === b.code ? 0 : -1);
    this.setMandateQualitatives(newAssignedDignities);

    this.setState({
      unassignedDignities: newUnassignedDignities,
      assignedDignities: newAssignedDignities,
      selectedAssignedDignities: [],
      selectedUnassignedDignities: []
    });
  };

  setMandateQualitatives = (assignedDignities: TarmedDignityDto[]) => {
    let mandateQualitatives: MandateQualitativeDto[] = [];
    assignedDignities.forEach(x => {
      mandateQualitatives.push({code: x.code})
    });
    this.props.setMandateQualitatives(mandateQualitatives);
  };

  noRowsRederer = (messageName: string) => {
    const {getMessage} = this.props;
    return (
        <StyledNoRowsMessage>
          {getMessage(`mandate.${messageName}`)}
        </StyledNoRowsMessage>
    );
  }

}

export default withMessages(DignitySection);
